/**
 * Iter XX.4 — Gully Plus Launch Visibility surfaces.
 *
 * Three pieces:
 *   · usePlusUpgradeSurfaces(sessionId) — hook that fetches the current
 *     surface booleans from the backend once per mount, and exposes
 *     helpers for dismissing / re-fetching.
 *   · <PlusLaunchBanner /> — dismissible top banner: "🚀 Gully Plus is
 *     now live". Fires plus_banner_shown on first render, plus_banner_
 *     dismissed on close, plus_upgrade_button_clicked on CTA press.
 *   · <PostReportUpgradeCard /> — subtle card that renders below a
 *     completed report bubble for free users, capped at 1 / 24h.
 *
 * All surfaces:
 *   · Are hidden for Plus subscribers (backend returns is_plus=true).
 *   · Track their own analytics events without blocking the UI.
 *   · Never interrupt the conversation flow.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Star } from "lucide-react";
import api from "../lib/api";

const track = (kind, sessionId, source) => {
  try {
    api
      .post("/plus/track", { kind, session_id: sessionId, source })
      .catch(() => {});
  } catch {
    /* swallow */
  }
};

export function usePlusUpgradeSurfaces(sessionId) {
  const [surfaces, setSurfaces] = useState(null);

  const refetch = useCallback(async () => {
    // Skip the fetch entirely until we have a real session_id — the
    // backend keys anonymous state on session_id, so a null-param fetch
    // would return the "brand new visitor" defaults and defeat the
    // 30-day dismissal suppression on the next reload.
    if (!sessionId) return;
    try {
      const r = await api.get(
        `/plus/upgrade-surfaces?session_id=${encodeURIComponent(sessionId)}`,
      );
      setSurfaces(r.data || null);
    } catch {
      setSurfaces(null);
    }
  }, [sessionId]);

  useEffect(() => {
    // Fires once sessionId lands (and again if it changes). Before that
    // we intentionally render no surfaces at all — the banner render is
    // gated on `surfaces?.show_launch_banner` so returning `null` here
    // keeps the UI blank and prevents the double-track issue.
    refetch();
  }, [refetch]);

  const dismissBanner = useCallback(async () => {
    try {
      await api.post("/plus/banner/dismiss", { session_id: sessionId });
    } catch {
      /* swallow */
    }
    setSurfaces((s) => (s ? { ...s, show_launch_banner: false } : s));
  }, [sessionId]);

  const markPostReportShown = useCallback(async () => {
    try {
      await api.post("/plus/post-report-card/shown", {
        session_id: sessionId,
      });
    } catch {
      /* swallow */
    }
    setSurfaces((s) =>
      s ? { ...s, show_post_report_card: false } : s
    );
  }, [sessionId]);

  return { surfaces, refetch, dismissBanner, markPostReportShown };
}

export function PlusLaunchBanner({ sessionId, onDismiss }) {
  const shownRef = useRef(false);
  useEffect(() => {
    // Guard: only fire the impression event once we actually have a
    // session_id to attribute it to. Prevents double-counting on cold
    // load where the banner mounts before sessionId is resolved.
    if (!sessionId) return;
    if (shownRef.current) return;
    shownRef.current = true;
    track("plus_banner_shown", sessionId, "chat_top_banner");
  }, [sessionId]);

  const handleUpgradeClick = () => {
    track("plus_upgrade_button_clicked", sessionId, "chat_top_banner");
  };

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
  };

  return (
    <div
      className="flex-none w-full border-b border-emerald-800/40 bg-gradient-to-r from-emerald-950/60 via-emerald-900/40 to-emerald-950/60 px-4 py-3"
      data-testid="plus-launch-banner"
    >
      <div className="flex items-start gap-3 max-w-4xl mx-auto">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-emerald-100 mb-0.5">
            <span className="mr-1.5">🏏</span>
            This is your cricket, remembered.
          </p>
          <p className="text-xs text-emerald-200/80 leading-relaxed">
            Come back and continue instead of starting from zero. Plus
            keeps your cricket between sessions.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-none">
          <Link
            to="/plus"
            onClick={handleUpgradeClick}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-semibold px-3 py-1.5 transition-colors whitespace-nowrap shadow-sm shadow-emerald-950/40"
            data-testid="plus-launch-banner-upgrade"
          >
            <Star size={12} className="fill-current" />
            Keep going with Plus
          </Link>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="p-1 rounded text-emerald-200/70 hover:text-white hover:bg-emerald-800/40 transition-colors"
            data-testid="plus-launch-banner-dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function PostReportUpgradeCard({ sessionId, onSeen }) {
  const seenRef = useRef(false);
  useEffect(() => {
    if (seenRef.current) return;
    seenRef.current = true;
    if (onSeen) onSeen();
  }, [onSeen]);

  const handleClick = () => {
    track("plus_upgrade_button_clicked", sessionId, "post_report_card");
  };

  return (
    <div
      className="my-3 rounded-2xl border border-emerald-800/40 bg-[#0f1a15] p-4"
      data-testid="plus-post-report-card"
    >
      <div className="flex items-start gap-3">
        <Sparkles size={18} className="text-emerald-400 flex-none mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-neutral-200 font-medium mb-1">
            Keep working on this after today’s chat?
          </p>
          <ul className="text-xs text-neutral-400 space-y-0.5 mb-3 leading-relaxed">
            <li>· Continue this focus next time instead of restarting</li>
            <li>· See recurring problems across matches</li>
            <li>· Track whether something is actually changing</li>
            <li>· Every video review builds on your last one</li>
          </ul>
          <Link
            to="/plus"
            onClick={handleClick}
            className="inline-flex items-center gap-1 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-semibold px-3 py-1.5 transition-colors"
            data-testid="plus-post-report-card-upgrade"
          >
            <Star size={12} className="fill-current" />
            Continue with Plus
          </Link>
        </div>
      </div>
    </div>
  );
}
